import { connect } from 'react-redux'
import { getDistance, convertDistance } from 'geolib'
import lodashGet from 'lodash/get'
import apiUrl from 'base/api_url'
import { get } from 'base/lib/network'
import store from 'base/store'
import { getPrimaryAddress } from 'base/reducers/session/addresses'

const defaultLocalization = {
  currency: 'USD',
  country_code: 'US',
  symbol: '$',
  units: 2,
  shipping_policy: 'Free shipping on your first order',
  is_default: true,
}

export const DISTANCE_THRESHOLD = 50 // only show stores within this mile threshold

export function getLocalization() {
  const visitor = lodashGet(store.getState(), 'session.visitor')
  if (visitor.localization) {
    return visitor.localization
  } else {
    return defaultLocalization
  }
}

export function getDistanceFromUser({ latitude, longitude }) {
  if (!latitude || !longitude) {
    return Number.MAX_SAFE_INTEGER
  }

  const visitor = lodashGet(store.getState(), 'session.visitor')
  if (visitor.localization && visitor.localization.lat_long) {
    const visitorCoordinates = visitor.localization.lat_long.split(',')
    const distance = getDistance(
      {
        latitude: visitorCoordinates[0],
        longitude: visitorCoordinates[1],
      },
      { latitude, longitude },
    )
    return convertDistance(distance, 'mi')
  } else {
    return Number.MAX_SAFE_INTEGER
  }
}

export function getCountryCode() {
  return getLocalization().country_code
}

export function getLanguage() {
  let language = 'en_US'

  if (getLocalization().country_code === 'CN') {
    language = 'zh_CN'
  }
  return language
}

export function getCountryName(countryCode) {
  const allCountries = store.getState().shippingConstants.all_countries

  if (!allCountries) {
    return null
  } else if (countryCode) {
    return allCountries[countryCode]
  } else {
    return allCountries[getCountryCode()]
  }
}

export function getShippingCountry(addresses = [], cartAddress = {}) {
  const primaryAddress = getPrimaryAddress(addresses)

  return primaryAddress?.country || cartAddress?.country || getLocalization().country_code
}

// TODO: we can replace the getLocalization call if we have the
// ISO 4217 country code by using the 'currency' optional param
export function formatCurrencyWithSymbol(num, options = { decimalDigits: 0 }) {
  const amount = new Intl.NumberFormat(undefined, {
    style: 'decimal',
    minimumFractionDigits: options.decimalDigits,
    maximumFractionDigits: options.decimalDigits,
  }).format(num)
  return `${getLocalization().symbol}${amount}`
}

export function everlaneRateForCurrentLocalization() {
  if (getCountryCode() === 'US') return 1.0
  const internationalShippingConfig =
    store.getState().shippingConstants.international_shipping_configuration
  const localeConfig = internationalShippingConfig[getCountryCode()]
  return localeConfig.everlane_rate
}

export function getUSDPrice(price) {
  return (price / everlaneRateForCurrentLocalization()).toFixed(2)
}

export async function fetchStoresSortedByDistance({ distanceThreshold = DISTANCE_THRESHOLD } = {}) {
  const { storefronts } = await get(apiUrl('storefronts'))

  const nearbyStores = storefronts
    .map(storefront => {
      const distanceFromUser = getDistanceFromUser({
        latitude: storefront.latitude,
        longitude: storefront.longitude,
      })
      return { ...storefront, distanceFromUser }
    })
    .filter(storefront => storefront.distanceFromUser <= distanceThreshold)
    .sort((a, b) => a.distanceFromUser - b.distanceFromUser)

  return nearbyStores
}

function mapStateToProps(state) {
  if (state.session && state.session.visitor) {
    return {
      localization: state.session.visitor.localization,
      cartAddress: state.cart?.shipping_address ?? {},
    }
  }
}

export function localizationAware(component) {
  return connect(mapStateToProps, null)(component)
}
